import type { SideMenu } from '#/public';
import { cloneDeep } from 'lodash';

const CONTENT_MENU_KEY = '/content';
const HOME_MENU_KEY = '/content/home';
const HOME_MENU_RULE = '/content/home';
const COMPANY_MENU_KEY = '/content/company';
const COMPANY_MENU_RULE = '/content/company';
const INDUSTRY_MENU_KEY = '/content/industry';
const INDUSTRY_MENU_RULE = '/content/industry';
const PRODUCT_MENU_KEY = '/content/product';
const PRODUCT_MENU_RULE = '/content/product';

const FREE_SAMPLE_MENU_KEY = '/content/freeSample';
const FREE_SAMPLE_MENU_RULE = '/content/freeSample';

const FAQ_MENU_KEY = '/content/faq';
const FAQ_MENU_RULE = '/content/faq';

const DOCUMENT_MENU_KEY = '/content/document';
const DOCUMENT_MENU_RULE = '/content/document';

const CONTACT_MENU_KEY = '/content/contact';
const CONTACT_MENU_RULE = '/content/contact';

/**
 * 递归搜索内容管理菜单
 * @param menus - 菜单数据
 */
function findContentMenu(menus: SideMenu[] | undefined): SideMenu | undefined {
  if (!menus?.length) return undefined;

  for (const item of menus) {
    if (
      item.key === CONTENT_MENU_KEY ||
      item.label === '内容管理' ||
      item.labelZh === '内容管理' ||
      item.labelEn?.toLowerCase() === 'content'
    ) {
      return item;
    }

    const child = findContentMenu(item.children);
    if (child) return child;
  }

  return undefined;
}

/**
 * 注入"首页配置"、"公司"、"应用行业配置"、"产品"、"免费打样"、"常见问题配置"、"文档下载配置"和"联系我们配置"子菜单
 * @param menus - 原始菜单
 */
export function withCompanyMenu(menus: SideMenu[] = []): SideMenu[] {
  if (!menus.length) return menus;

  const nextMenus = cloneDeep(menus);
  const contentMenu = findContentMenu(nextMenus);

  if (!contentMenu) return nextMenus;

  if (!contentMenu.children) contentMenu.children = [];

  const hasHomeMenu = contentMenu.children.some((child) => child.key === HOME_MENU_KEY);
  if (!hasHomeMenu) {
    contentMenu.children.push({
      label: '首页配置',
      labelZh: '首页配置',
      labelEn: 'Home Config',
      key: HOME_MENU_KEY,
      rule: HOME_MENU_RULE,
    });
  }

  const hasCompanyMenu = contentMenu.children.some((child) => child.key === COMPANY_MENU_KEY);
  if (!hasCompanyMenu) {
    contentMenu.children.push({
      label: '公司',
      labelZh: '公司',
      labelEn: 'Company',
      key: COMPANY_MENU_KEY,
      rule: COMPANY_MENU_RULE,
    });
  }

  const hasIndustryMenu = contentMenu.children.some((child) => child.key === INDUSTRY_MENU_KEY);
  if (!hasIndustryMenu) {
    contentMenu.children.push({
      label: '应用行业配置',
      labelZh: '应用行业配置',
      labelEn: 'Industry Config',
      key: INDUSTRY_MENU_KEY,
      rule: INDUSTRY_MENU_RULE,
    });
  }

  const hasProductMenu = contentMenu.children.some((child) => child.key === PRODUCT_MENU_KEY);
  if (!hasProductMenu) {
    contentMenu.children.push({
      label: '产品',
      labelZh: '产品',
      labelEn: 'Product',
      key: PRODUCT_MENU_KEY,
      rule: PRODUCT_MENU_RULE,
    });
  }

  const hasFreeSampleMenu = contentMenu.children.some((child) => child.key === FREE_SAMPLE_MENU_KEY);
  if (!hasFreeSampleMenu) {
    contentMenu.children.push({
      label: '免费打样配置',
      labelZh: '免费打样配置',
      labelEn: 'Free Sample',
      key: FREE_SAMPLE_MENU_KEY,
      rule: FREE_SAMPLE_MENU_RULE,
    });
  }

  // 处理常见问题配置菜单
  const faqMenuIndex = contentMenu.children.findIndex((child) => child.key === FAQ_MENU_KEY);
  if (faqMenuIndex === -1) {
    contentMenu.children.push({
      label: '常见问题配置',
      labelZh: '常见问题配置',
      labelEn: 'FAQ',
      key: FAQ_MENU_KEY,
      rule: FAQ_MENU_RULE,
    });
  } else {
    // 如果菜单已存在，确保 rule 字段正确设置
    contentMenu.children[faqMenuIndex].rule = FAQ_MENU_RULE;
  }

  // 处理文档下载配置菜单
  const documentMenuIndex = contentMenu.children.findIndex((child) => child.key === DOCUMENT_MENU_KEY);
  if (documentMenuIndex === -1) {
    contentMenu.children.push({
      label: '文档下载配置',
      labelZh: '文档下载配置',
      labelEn: 'Document Download',
      key: DOCUMENT_MENU_KEY,
      rule: DOCUMENT_MENU_RULE,
    });
  } else {
    // 如果菜单已存在，确保 rule 字段正确设置
    contentMenu.children[documentMenuIndex].rule = DOCUMENT_MENU_RULE;
  }

  // 处理联系我们配置菜单
  const contactMenuIndex = contentMenu.children.findIndex((child) => child.key === CONTACT_MENU_KEY);
  if (contactMenuIndex === -1) {
    contentMenu.children.push({
      label: '联系我们配置',
      labelZh: '联系我们配置',
      labelEn: 'Contact Us',
      key: CONTACT_MENU_KEY,
      rule: CONTACT_MENU_RULE,
    });
  } else {
    // 如果菜单已存在，确保 rule 字段正确设置
    contentMenu.children[contactMenuIndex].rule = CONTACT_MENU_RULE;
  }

  return nextMenus;
}

/**
 * 合并"首页配置"、"公司"、"应用行业配置"、"产品"、"免费打样"、"常见问题配置"、"文档下载配置"和"联系我们配置"相关权限，避免菜单被权限过滤
 * 注意：此函数已废弃，不再自动添加权限，权限应该由后端返回
 * @param permissions - 原始权限
 * @deprecated 不再自动添加权限，直接返回原始权限
 */
export function withCompanyPermissions(permissions: string[] = []): string[] {
  // 不再自动添加权限，直接返回后端返回的权限
  // 这样可以确保权限控制生效
  return permissions;
}

